import { trackEvent } from "~/jose_modern/analytics.js";
import { Flash } from "~/jose_modern/flash.js";
import { StashedFile } from "~/jose_modern/stashed_file.js";
import { requestJson } from "../shared/requests.js";
import {
  ingredientPath,
  agentSessionRecipePath,
  createAgentSessionRecipePath,
  applyActionsRecipePath,
  stashedFilePath,
} from "routes";

const CONSTANTS = {
  MAXIMUM_WAIT_SECONDS: 60,
  APPLY_POLL_DELAYS_MS: [250, 250, 500, 500],
  PARSE_BACKOFF_SECONDS: 5,
  FETCH_TIMEOUT_SECONDS: 15,
  ERRORS: {
    TIMEOUT: "This is taking too long. Please try again.",
    PROCESSING: "Failed to process ingredients. Please try again.",
    EMPTY_INGREDIENT_LIST: "We were unable to read your ingredient list.",
  },
};

export function bindJumpstart(container) {
  const modal = container.querySelector(".modal");
  const elements = {
    form: container.querySelector("form"),
    input: container.querySelector("textarea"),
    modal,
    loadingBox: modal.querySelector(".loading"),
    applyLoading: modal.querySelector(".apply-loading"),
    ingredientSelection: modal.querySelector(".ingredient-selection"),
    yieldDefinition: modal.querySelector(".yield-definition"),
    ingredientForm: modal.querySelector(".ingredients"),
    buttons: {
      add: modal.querySelector("button.add-ingredients"),
      cancel: modal.querySelector("button.cancel"),
      close: modal.querySelector("button.delete"),
      done: modal.querySelector("button.done"),
      skip: modal.querySelector("button.skip"),
      yieldDefinitionClose: modal.querySelector(".yield-definition button.delete"),
    },
  };

  // Initialize yield definition functionality
  initYieldDefinition(elements);

  let collectedActions = [];
  const recipeID = elements.form.dataset.recipeId;

  // Modal management
  const modalController = {
    dismiss: () => {
      elements.modal.classList.remove("is-active");
      document.removeEventListener("keydown", handleKeydown);
    },
    cancel: () => {
      trackEvent("jumpstart_cancel");
      elements.modal.classList.remove("is-active");
      document.removeEventListener("keydown", handleKeydown);
    },
    setLoading: (isLoading) => {
      elements.loadingBox.classList.toggle("is-hidden", !isLoading);
      elements.ingredientSelection.classList.toggle("is-hidden", isLoading);
      elements.yieldDefinition.classList.add("is-hidden");
      elements.modal.querySelector(".modal-content").classList.toggle("is-loading", isLoading);

      if (!isLoading) {
        trackEvent("jumpstart_display_ingredients");
      }
    },
    showYieldStep: () => {
      trackEvent("jumpstart_display_yield");
      elements.loadingBox.classList.add("is-hidden");
      elements.ingredientSelection.classList.add("is-hidden");
      elements.yieldDefinition.classList.remove("is-hidden");
      elements.modal.querySelector(".modal-content").classList.add("is-hidden");
    },
    showApplyProgress: () => {
      elements.ingredientSelection.classList.add("is-hidden");
      elements.yieldDefinition.classList.add("is-hidden");
      elements.applyLoading.classList.remove("is-hidden");
    },
    disableButtons: (disabled) => {
      elements.modal.querySelectorAll("button").forEach((b) => (b.disabled = disabled));
    },
  };

  const handleKeydown = (e) => {
    if (e.key === "Escape") modalController.cancel();
  };

  // Event listeners
  document.addEventListener("keydown", handleKeydown);

  elements.buttons.add.addEventListener("click", async () => {
    if (!elements.ingredientForm.reportValidity()) {
      return;
    }

    trackEvent("jumpstart_picked_ingredients");

    modalController.showYieldStep();
  });

  const applyAndReload = async (includeYield = true) => {
    modalController.disableButtons(true);

    collectedActions = collectActions(elements.ingredientSelection);

    trackEvent("jumpstart_add_ingredients");

    // Add yield action if available
    const yieldAction = collectYieldData(elements.yieldDefinition);
    if (includeYield && yieldAction) {
      collectedActions.push(yieldAction);

      trackEvent("jumpstart_set_yield");
    }

    // Add save recipe action
    collectedActions.push({ name: "save_recipe", arguments: {} });
    modalController.showApplyProgress();

    try {
      const { response, data } = await requestJson(applyActionsRecipePath(recipeID), {
        method: "POST",
        body: JSON.stringify({ actions: collectedActions }),
      });
      if (!response.ok || !data) throw new Error(CONSTANTS.ERRORS.PROCESSING);

      const { token } = data;
      await StashedFile.download({
        path: stashedFilePath(token),
        emitEvents: false,
        pollDelays: CONSTANTS.APPLY_POLL_DELAYS_MS,
      });

      trackEvent("jumpstart_done");
      window.location.reload();
    } catch {
      trackEvent("jumpstart_apply_error");
      Flash.error(CONSTANTS.ERRORS.PROCESSING);
      modalController.dismiss();
    }
  };

  elements.buttons.done.addEventListener("click", () => {
    const validation = validateYieldDefinition(elements.yieldDefinition);
    if (!validation.valid) {
      markInvalidFields(validation.invalidFields);
      return;
    }

    applyAndReload();
  });

  elements.buttons.skip.addEventListener("click", () => {
    trackEvent("jumpstart_skip_yield");

    applyAndReload(false);
  });

  [elements.buttons.cancel, elements.buttons.close, elements.buttons.yieldDefinitionClose].forEach(
    (button) => {
      button.addEventListener("click", modalController.cancel);
    }
  );

  elements.form.addEventListener("submit", async (e) => {
    e.preventDefault();
    if (!elements.input.value.trim()) {
      Flash.error("Please enter ingredients");
      return;
    }

    trackEvent("jumpstart_search");

    elements.applyLoading.classList.add("is-hidden");
    elements.modal.classList.add("is-active");
    modalController.setLoading(true);

    try {
      const response = await fetch(createAgentSessionRecipePath(recipeID), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          command: "populate_from_ingredients",
          dry_run: "true",
          arguments: { ingredient_list: elements.input.value },
        }),
      });

      const session = await response.json();
      const result = await pollForResult(recipeID, session.id);

      modalController.setLoading(false);
      modalController.disableButtons(false);

      populateIngredientSelection(elements.ingredientSelection, result.view_model);
      populateYieldDefinition(elements.yieldDefinition, result.view_model);
    } catch (error) {
      if (Object.values(CONSTANTS.ERRORS).includes(error.message)) {
        Flash.error(error.message);
      } else {
        Flash.error(CONSTANTS.ERRORS.PROCESSING);
      }
      modalController.dismiss();
    }
  });
}

async function pollForResult(recipeID, sessionID) {
  const maxAttempts = CONSTANTS.MAXIMUM_WAIT_SECONDS / CONSTANTS.PARSE_BACKOFF_SECONDS;
  let attempts = 0;

  const fetchWithTimeout = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CONSTANTS.FETCH_TIMEOUT_SECONDS * 1000);

    try {
      const response = await fetch(agentSessionRecipePath(recipeID, { session_id: sessionID }), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(CONSTANTS.ERRORS.PROCESSING);
      }
      return await response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  };

  while (attempts < maxAttempts) {
    try {
      const session = await fetchWithTimeout();

      switch (session.status) {
        case "pending":
        case "in_progress":
          attempts += 1;
          await new Promise((resolve) =>
            setTimeout(resolve, CONSTANTS.PARSE_BACKOFF_SECONDS * 1000)
          );
          break;
        case "complete":
          return session;
        default:
          throw new Error(CONSTANTS.ERRORS.PROCESSING);
      }
    } catch (error) {
      if (error.name === "AbortError") {
        throw new Error(CONSTANTS.ERRORS.TIMEOUT);
      }
      throw error;
    }
  }

  trackEvent("jumpstart_timeout");

  throw new Error(CONSTANTS.ERRORS.TIMEOUT);
}

function populateIngredientSelection(container, data) {
  const { parsed_ingredients = [], ingredients = {}, add_actions = {} } = data;
  if (parsed_ingredients.length === 0) {
    throw new Error(CONSTANTS.ERRORS.EMPTY_INGREDIENT_LIST);
  }

  const elements = {
    ingredients: container.querySelector(".ingredients"),
    templates: {
      notFound: container.querySelector("template.not-found"),
      ingredient: container.querySelector("template.ingredient"),
    },
  };

  // Reset and prepare container
  elements.ingredients.classList.add("fade-in");
  elements.ingredients.replaceChildren();

  const createIngredientRows = parsed_ingredients.map((parsedIngredient) => {
    const action = add_actions[parsedIngredient];
    return action
      ? createIngredientRow(
          elements.templates.ingredient,
          parsedIngredient,
          ingredients[action.ingredient_id],
          action
        )
      : createNotFoundRow(elements.templates.notFound, parsedIngredient);
  });

  elements.ingredients.append(...createIngredientRows);
}

function createIngredientRow(template, query, ingredient, action) {
  if (!ingredient || !action) {
    throw new Error("Invalid ingredient data provided");
  }

  const row = template.content.cloneNode(true);
  const elements = {
    ingredientId: row.querySelector("input[name=ingredient_id]"),
    query: row.querySelector(".query"),
    name: row.querySelector(".name"),
    quantity: row.querySelector(".quantity"),
    unitSelect: row.querySelector(".units select"),
  };
  elements.ingredientId.value = ingredient.id;
  elements.query.innerText = query;
  elements.name.innerText = ingredient.name;
  elements.name.href = ingredientPath(action.ingredient_id);
  elements.quantity.value = Number(action.quantity).toFixed(
    Number.isInteger(action.quantity) ? 0 : 2
  );

  const unitOptions = (ingredient.units || []).map((unit) => {
    const option = document.createElement("option");
    option.value = unit;
    option.textContent = unit;
    option.selected = unit === action.unit;
    return option;
  });

  elements.unitSelect.append(...unitOptions);

  return row;
}

function createNotFoundRow(template, query) {
  const row = template.content.cloneNode(true);
  row.querySelector(".query").innerText = query;
  return row;
}

function collectActions(container) {
  const ingredientRows = container.querySelectorAll(".ingredients .ingredient");

  const ingredientActions = Array.from(ingredientRows)
    .map((row) => {
      const checkbox = row.querySelector("input[type=checkbox]");
      if (!checkbox) {
        return null;
      }
      if (!checkbox?.checked) {
        return null;
      }

      const quantity = parseFloatFixed(row.querySelector(".quantity").value);
      if (isNaN(quantity) || quantity <= 0) {
        return null;
      }

      return {
        name: "add_ingredient_to_recipe",
        arguments: {
          ingredient_id: row.querySelector("input[name=ingredient_id]").value,
          quantity,
          unit: row.querySelector(".units select").value,
        },
      };
    })
    .filter((action) => action !== null);

  return ingredientActions;
}

function initYieldDefinition(elements) {
  const yieldDefinition = elements.yieldDefinition;
  const yieldOptions = yieldDefinition.querySelectorAll('input[name="yield_type"]');

  function toggleYieldOptions(selected) {
    const selectedValue = selected.value;

    // Clear any validation markers when switching options
    document.querySelectorAll(".yield-definition input.is-danger").forEach((field) => {
      field.classList.remove("is-danger");
    });

    // Process each yield option
    yieldOptions.forEach((option) => {
      const optionValue = option.value;
      const yieldBlock = option.closest(".yield-option");
      const isSelected = optionValue === selectedValue;

      // Find all inputs related to this option
      const inputs = yieldBlock.querySelectorAll('input:not([type="radio"]), select');

      // Enable/disable inputs
      inputs.forEach((input) => {
        input.disabled = !isSelected;
      });

      // Adjust opacity
      option.closest(".yield-option").style.opacity = isSelected ? "1" : "0.7";
    });
  }

  // Add change event listener to all radio buttons
  yieldOptions.forEach((option) => {
    option.addEventListener("change", (e) => {
      if (e.target.checked) {
        toggleYieldOptions(e.target);
      }
    });
  });

  // Initialize with the first option selected if none are
  const selectedOption = Array.from(yieldOptions).find((opt) => opt.checked);
  if (selectedOption) {
    toggleYieldOptions(selectedOption);
  } else if (yieldOptions.length > 0) {
    yieldOptions[0].checked = true;
    toggleYieldOptions(yieldOptions[0]);
  }
}

function resetYieldDefinition(container) {
  container.querySelectorAll('input[type="number"]').forEach((input) => {
    input.value = "";
    input.classList.remove("is-danger"); // Remove validation styling
  });

  container.querySelectorAll("select").forEach((select) => {
    if (select.options.length > 0) {
      select.selectedIndex = 0;
    }
  });

  const radioButtons = container.querySelectorAll('input[name="yield_type"]');
  if (radioButtons.length > 0) {
    radioButtons[0].checked = true;
    radioButtons[0].dispatchEvent(new Event("change"));
  }
}

function populateYieldDefinition(container, data) {
  const yieldActions = data.yield_actions;

  if (!yieldActions) return;

  resetYieldDefinition(container);

  const priorities = {
    by_servings: 1,
    by_serving_size: 2,
    by_packaging: 3,
  };

  let selectedOptionType = null;
  let highestPriority = 0;

  for (const yieldAction of yieldActions) {
    const actionName = yieldAction.name;
    const args = yieldAction.arguments || {};

    switch (actionName) {
      case "set_yield_by_servings":
        if (priorities["by_servings"] > highestPriority) {
          highestPriority = priorities["by_servings"];
          selectedOptionType = "by_servings";
        }
        container.querySelector('[name="servings"]').value = args.servings || "";
        break;

      case "set_yield_by_serving_size":
        if (priorities["by_serving_size"] > highestPriority) {
          highestPriority = priorities["by_serving_size"];
          selectedOptionType = "by_serving_size";
        }
        container.querySelector('[name="serving_size_amount"]').value =
          args.serving_size_quantity || "";
        container.querySelector('[name="serving_size_unit"]').value =
          args.serving_size_unit || "grams";
        container.querySelector('[name="serving_size_servings"]').value =
          args.servings_per_package || "";
        break;

      case "set_yield_by_package_size":
        if (priorities["by_packaging"] > highestPriority) {
          highestPriority = priorities["by_packaging"];
          selectedOptionType = "by_packaging";
        }
        container.querySelector('[name="package_size_amount"]').value =
          args.package_yield_quantity || "";
        container.querySelector('[name="package_size_unit"]').value =
          args.package_yield_unit || "grams";
        container.querySelector('[name="package_count"]').value = args.package_count || "";
        container.querySelector('[name="package_servings"]').value =
          args.servings_per_package || "";
        break;
    }
  }

  if (selectedOptionType) {
    const radioToSelect = container.querySelector(
      `[name="yield_type"][value="${selectedOptionType}"]`
    );
    if (radioToSelect) {
      container.querySelectorAll('[name="yield_type"]').forEach((radio) => {
        radio.checked = false;
      });

      radioToSelect.checked = true;

      radioToSelect.dispatchEvent(new Event("change"));
    }
  }
}

function validateYieldDefinition(container) {
  const selectedOption = container.querySelector('input[name="yield_type"]:checked');
  if (!selectedOption) {
    return { valid: false, invalidFields: [] };
  }

  const yieldType = selectedOption.value;
  const invalidFields = [];

  if (yieldType === "by_servings") {
    const servings = container.querySelector('[name="servings"]');
    if (
      !servings.value ||
      isNaN(parseFloatFixed(servings.value)) ||
      parseFloatFixed(servings.value) < 1
    ) {
      invalidFields.push(servings);
    }
  }

  if (yieldType === "by_serving_size") {
    const servings = container.querySelector('[name="serving_size_servings"]');
    const servingSize = container.querySelector('[name="serving_size_amount"]');

    if (
      !servings.value ||
      isNaN(parseFloatFixed(servings.value)) ||
      parseFloatFixed(servings.value) < 1
    ) {
      invalidFields.push(servings);
    }

    if (
      !servingSize.value ||
      isNaN(parseFloatFixed(servingSize.value)) ||
      parseFloatFixed(servingSize.value) <= 0
    ) {
      invalidFields.push(servingSize);
    }
  }

  if (yieldType === "by_packaging") {
    const packageCount = container.querySelector('[name="package_count"]');
    const packageSize = container.querySelector('[name="package_size_amount"]');
    const packageServings = container.querySelector('[name="package_servings"]');

    if (
      !packageCount.value ||
      isNaN(parseFloatFixed(packageCount.value)) ||
      parseFloatFixed(packageCount.value, 10) < 1
    ) {
      invalidFields.push(packageCount);
    }

    if (
      !packageSize.value ||
      isNaN(parseFloatFixed(packageSize.value)) ||
      parseFloatFixed(packageSize.value) <= 0
    ) {
      invalidFields.push(packageSize);
    }

    if (
      !packageServings.value ||
      isNaN(parseFloatFixed(packageServings.value)) ||
      parseFloatFixed(packageServings.value) < 1
    ) {
      invalidFields.push(packageServings);
    }
  }

  return {
    valid: invalidFields.length === 0,
    invalidFields,
  };
}

// Function to mark invalid fields
function markInvalidFields(invalidFields) {
  // First clear any previous validation markers
  document.querySelectorAll(".yield-definition input.is-danger").forEach((field) => {
    field.classList.remove("is-danger");
  });

  // Mark each invalid field
  invalidFields.forEach((field) => {
    field.classList.add("is-danger");
  });
}

// Function to collect yield data and return appropriate action
function collectYieldData(container) {
  const selectedOption = container.querySelector('input[name="yield_type"]:checked');

  if (!selectedOption) {
    return null; // No option selected
  }

  const yieldType = selectedOption.value;

  if (yieldType === "by_servings") {
    return {
      name: "set_yield_by_servings",
      arguments: {
        servings: parseFloatFixed(container.querySelector('[name="servings"]').value) || 1,
      },
    };
  }

  if (yieldType === "by_serving_size") {
    return {
      name: "set_yield_by_serving_size",
      arguments: {
        serving_size_quantity:
          parseFloatFixed(container.querySelector('[name="serving_size_amount"]').value) || 0,
        serving_size_unit: container.querySelector('[name="serving_size_unit"]').value,
        servings_per_package:
          parseFloatFixed(container.querySelector('[name="serving_size_servings"]').value, 10) || 1,
      },
    };
  }

  if (yieldType === "by_packaging") {
    return {
      name: "set_yield_by_package_size",
      arguments: {
        package_yield_quantity:
          parseFloatFixed(container.querySelector('[name="package_size_amount"]').value) || 0,
        package_yield_unit: container.querySelector('[name="package_size_unit"]').value,
        package_count:
          parseFloatFixed(container.querySelector('[name="package_count"]').value) || 1,
        servings_per_package:
          parseFloatFixed(container.querySelector('[name="package_servings"]').value, 10) || 1,
      },
    };
  }

  return null;
}

function parseFloatFixed(str) {
  return parseFloatWithPrecision(str, 4);
}

function parseFloatWithPrecision(str, decimals) {
  const factor = Math.pow(10, decimals);
  return Math.round(parseFloat(str) * factor) / factor;
}
